/**
 * ESRS E5 — Resource Use & Circular Economy
 *
 * E5-4 resource inflows (biological share, sustainable sourcing, secondary
 * content) and E5-5 resource outflows: waste by hazard class and treatment
 * route plus product-outflow durability and recyclable content. Hazardous +
 * radioactive waste feeds SFDR PAI 9 (see backend/services/esrs_e5_service.py).
 *
 * Backend contract (backend/routes/esrs_e5_routes.py):
 *   GET  /api/esrs/e5/disclosure        → { esrs_e5_4_disclosure, esrs_e5_5_disclosure,
 *                                           sfdr_pai_9, recommendations }
 *   GET  /api/esrs/e5/records           → { waste: [...], inflows: [...], products: [...] }
 *   POST /api/esrs/e5/waste-records     → waste record
 *   POST /api/esrs/e5/resource-inflows  → inflow record
 *   POST /api/esrs/e5/product-outflows  → product record
 */
import { useCallback, useEffect, useState } from "react";
import {
  Recycle, AlertTriangle, Radiation, Leaf, Trash2, Plus, X, Gauge,
  Package, Boxes, Lightbulb,
} from "lucide-react";
import {
  Page, PageHeader, Card, Overline, Metric, Badge, Data, Btn, DataTable,
  TabBar, EmptyState, ErrorBanner, Skeleton, field, Label, apiFetch, fmt, C,
  SANS,
} from "../../../components/esg/EsgKit";
import { useEntities } from "../../../hooks/useEntities";

const HAZARD_OPTIONS = [
  { value: "non_hazardous", label: "Non-hazardous" },
  { value: "hazardous", label: "Hazardous" },
  { value: "radioactive", label: "Radioactive" },
];

const TREATMENT_OPTIONS = [
  { value: "preparation_for_reuse", label: "Preparation for reuse" },
  { value: "recycling", label: "Recycling" },
  { value: "other_recovery", label: "Other recovery" },
  { value: "incineration", label: "Incineration" },
  { value: "landfill", label: "Landfill" },
  { value: "other_disposal", label: "Other disposal" },
];

const METHOD_OPTIONS = [
  { value: "measured", label: "Measured" },
  { value: "calculated", label: "Calculated" },
  { value: "estimated", label: "Estimated" },
];

const treatmentLabel = (v) =>
  TREATMENT_OPTIONS.find((o) => o.value === v)?.label || v || "—";

const methodLabel = (v) =>
  METHOD_OPTIONS.find((o) => o.value === v)?.label || v || "—";

function hazardBadge(cls) {
  switch (cls) {
    case "radioactive":
      return <Badge c={C.destructive} Icon={Radiation} label="Radioactive" />;
    case "hazardous":
      return <Badge c={C.accent} Icon={AlertTriangle} label="Hazardous" />;
    case "non_hazardous":
      return <Badge c={C.neutral} Icon={Trash2} label="Non-hazardous" />;
    default:
      return <Badge c={C.neutral} Icon={Trash2} label={cls || "—"} />;
  }
}

const divertedBadge = (diverted) =>
  diverted ? (
    <Badge c={C.primary} Icon={Recycle} label="Diverted" />
  ) : (
    <Badge c={C.neutral} Icon={Trash2} label="Disposal" />
  );

function biologicalBadge(r) {
  if (!r.biological) return <Badge c={C.neutral} Icon={Boxes} label="Non-biological" />;
  if (r.sustainably_sourced) return <Badge c={C.primary} Icon={Leaf} label="Sustainably sourced" />;
  return <Badge c={C.accent} Icon={Leaf} label="Biological" />;
}

const num = (v) => <Data color={C.body}>{fmt(v)}</Data>;
const pct = (v, d = 1) =>
  v === null || v === undefined ? "—" : fmt(v, d);

const EMPTY_WASTE_FORM = {
  facility_id: "",
  facility_name: "",
  waste_stream: "",
  ewc_code: "",
  hazard_class: "non_hazardous",
  treatment_route: "recycling",
  quantity_tonnes: "",
  measurement_method: "measured",
};

const EMPTY_INFLOW_FORM = {
  facility_id: "",
  material_name: "",
  quantity_tonnes: "",
  biological: "false",
  sustainably_sourced: "",
  secondary_content_tonnes: "",
  measurement_method: "measured",
};

const EMPTY_PRODUCT_FORM = {
  facility_id: "",
  product_line: "",
  quantity_tonnes: "",
  expected_durability_years: "",
  industry_avg_durability_years: "",
  recyclable_content_product_pct: "",
  recyclable_content_packaging_pct: "",
};

export default function E5CircularEconomy() {
  const { entities, entityId, setEntityId } = useEntities("esrs_e5_entity_id");
  const [period, setPeriod] = useState("FY2026");
  const [periodDraft, setPeriodDraft] = useState("FY2026");

  const [disclosure, setDisclosure] = useState(null);
  const [waste, setWaste] = useState([]);
  const [inflows, setInflows] = useState([]);
  const [products, setProducts] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [tab, setTab] = useState("waste");
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [wasteForm, setWasteForm] = useState(EMPTY_WASTE_FORM);
  const [inflowForm, setInflowForm] = useState(EMPTY_INFLOW_FORM);
  const [productForm, setProductForm] = useState(EMPTY_PRODUCT_FORM);

  const load = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    setError("");
    const q = `entity_id=${encodeURIComponent(entityId)}&reporting_period=${encodeURIComponent(period)}`;
    try {
      const [disc, recs] = await Promise.all([
        apiFetch(`/api/esrs/e5/disclosure?${q}`),
        apiFetch(`/api/esrs/e5/records?${q}`),
      ]);
      setDisclosure(disc || null);
      setWaste(recs?.waste || []);
      setInflows(recs?.inflows || []);
      setProducts(recs?.products || []);
    } catch (e) {
      setError(e.message || "Failed to load circular-economy data");
      setDisclosure(null);
      setWaste([]);
      setInflows([]);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [entityId, period]);

  useEffect(() => {
    load();
  }, [load]);

  const changeTab = (id) => {
    setTab(id);
    setShowForm(false);
  };

  const setWasteField = (k, v) => setWasteForm((f) => ({ ...f, [k]: v }));
  const setInflowField = (k, v) => setInflowForm((f) => ({ ...f, [k]: v }));
  const setProductField = (k, v) => setProductForm((f) => ({ ...f, [k]: v }));

  const post = async (path, body, resetForm) => {
    setSaving(true);
    setError("");
    try {
      await apiFetch(path, { method: "POST", body });
      resetForm();
      setShowForm(false);
      await load();
    } catch (err) {
      setError(err.message || "Failed to record data");
    } finally {
      setSaving(false);
    }
  };

  const submitWaste = (e) => {
    e.preventDefault();
    if (!entityId) return;
    post(
      "/api/esrs/e5/waste-records",
      {
        entity_id: entityId,
        reporting_period: period,
        facility_id: wasteForm.facility_id.trim(),
        facility_name: wasteForm.facility_name.trim(),
        waste_stream: wasteForm.waste_stream.trim(),
        ...(wasteForm.ewc_code.trim() ? { ewc_code: wasteForm.ewc_code.trim() } : {}),
        hazard_class: wasteForm.hazard_class,
        treatment_route: wasteForm.treatment_route,
        quantity_tonnes: Number(wasteForm.quantity_tonnes || 0),
        measurement_method: wasteForm.measurement_method,
      },
      () => setWasteForm(EMPTY_WASTE_FORM),
    );
  };

  const submitInflow = (e) => {
    e.preventDefault();
    if (!entityId) return;
    const biological = inflowForm.biological === "true";
    post(
      "/api/esrs/e5/resource-inflows",
      {
        entity_id: entityId,
        reporting_period: period,
        facility_id: inflowForm.facility_id.trim(),
        material_name: inflowForm.material_name.trim(),
        quantity_tonnes: Number(inflowForm.quantity_tonnes || 0),
        biological,
        ...(biological && inflowForm.sustainably_sourced !== ""
          ? { sustainably_sourced: inflowForm.sustainably_sourced === "true" }
          : {}),
        secondary_content_tonnes: Number(inflowForm.secondary_content_tonnes || 0),
        measurement_method: inflowForm.measurement_method,
      },
      () => setInflowForm(EMPTY_INFLOW_FORM),
    );
  };

  const submitProduct = (e) => {
    e.preventDefault();
    if (!entityId) return;
    const opt = (k, v) => (v === "" ? {} : { [k]: Number(v) });
    post(
      "/api/esrs/e5/product-outflows",
      {
        entity_id: entityId,
        reporting_period: period,
        facility_id: productForm.facility_id.trim(),
        product_line: productForm.product_line.trim(),
        quantity_tonnes: Number(productForm.quantity_tonnes || 0),
        ...opt("expected_durability_years", productForm.expected_durability_years),
        ...opt("industry_avg_durability_years", productForm.industry_avg_durability_years),
        ...opt("recyclable_content_product_pct", productForm.recyclable_content_product_pct),
        ...opt("recyclable_content_packaging_pct", productForm.recyclable_content_packaging_pct),
      },
      () => setProductForm(EMPTY_PRODUCT_FORM),
    );
  };

  const periodInput = (
    <input
      value={periodDraft}
      onChange={(e) => setPeriodDraft(e.target.value)}
      onBlur={() => setPeriod(periodDraft)}
      onKeyDown={(e) => { if (e.key === "Enter") setPeriod(periodDraft); }}
      aria-label="Reporting period"
      placeholder="FY2026"
      style={{ ...field, width: 110, textAlign: "center" }}
    />
  );

  const header = (
    <PageHeader
      overline="ESRS E5 · Resource Use & Circular Economy"
      OverlineIcon={Recycle}
      title="Resource Use & Circular Economy"
      subtitle="E5-4 resource inflows (biological share, sustainable sourcing, secondary content) and E5-5 outflows — waste by hazard class and treatment route, product durability and recyclable content. Hazardous and radioactive waste feeds SFDR PAI 9."
      entities={entities}
      entityId={entityId}
      onEntityChange={setEntityId}
      onRefresh={load}
      loading={loading}
      actions={periodInput}
    />
  );

  if (!entityId) {
    return (
      <Page>
        {header}
        <Card>
          <EmptyState
            Icon={Recycle}
            title="No reporting entity selected"
            body="Choose a reporting entity to view its ESRS E5 resource inflows, waste accounting, product outflows and SFDR PAI 9 feed."
          />
        </Card>
      </Page>
    );
  }

  const d4 = disclosure?.esrs_e5_4_disclosure || {};
  const dw = disclosure?.esrs_e5_5_disclosure?.waste || {};
  const dp = disclosure?.esrs_e5_5_disclosure?.products || {};
  const pai9 = disclosure?.sfdr_pai_9 || {};
  const recommendations = disclosure?.recommendations || [];
  const routes = dw.by_treatment_route_tonnes || {};

  const kpis = [
    { label: "Total inflows", value: fmt(d4.total_inflows_tonnes), unit: "t" },
    { label: "Secondary content", value: pct(d4.secondary_content_pct), unit: "%", color: C.primary },
    { label: "Biological share", value: pct(d4.biological_pct), unit: "%" },
    { label: "Total waste", value: fmt(dw.total_waste_tonnes), unit: "t" },
    { label: "Diverted from disposal", value: pct(dw.diversion_pct), unit: "%", color: C.primary },
    {
      label: "Non-recycled waste",
      value: pct(dw.non_recycled_waste_pct),
      unit: "%",
      color: (dw.non_recycled_waste_pct || 0) > 0 ? C.destructive : C.ink,
    },
    {
      label: "PAI 9 · hazardous + radioactive",
      value: fmt(pai9.hazardous_and_radioactive_tonnes),
      unit: "t",
      color: (pai9.hazardous_and_radioactive_tonnes || 0) > 0 ? C.destructive : C.ink,
    },
  ];

  const wasteColumns = [
    {
      key: "facility",
      label: "Facility",
      render: (r) => <Data>{r.facility_name || r.facility_id || "—"}</Data>,
    },
    {
      key: "waste_stream",
      label: "Waste stream",
      render: (r) => (
        <div>
          <Data color={C.body}>{r.waste_stream || "—"}</Data>
          {r.ewc_code && (
            <div>
              <Data size={10} color={C.muted}>EWC {r.ewc_code}</Data>
            </div>
          )}
        </div>
      ),
    },
    {
      key: "hazard_class",
      label: "Hazard class",
      render: (r) => hazardBadge(r.hazard_class),
    },
    {
      key: "treatment_route",
      label: "Treatment route",
      render: (r) => <Data color={C.body}>{treatmentLabel(r.treatment_route)}</Data>,
    },
    { key: "quantity_tonnes", label: "Quantity (t)", align: "right", render: (r) => num(r.quantity_tonnes) },
    {
      key: "diverted_from_disposal",
      label: "Destination",
      render: (r) => divertedBadge(r.diverted_from_disposal),
    },
    {
      key: "measurement_method",
      label: "Method",
      render: (r) => <Data color={C.body}>{methodLabel(r.measurement_method)}</Data>,
    },
  ];

  const inflowColumns = [
    {
      key: "facility_id",
      label: "Facility",
      render: (r) => <Data>{r.facility_id || "—"}</Data>,
    },
    {
      key: "material_name",
      label: "Material",
      render: (r) => <Data color={C.body}>{r.material_name || "—"}</Data>,
    },
    { key: "quantity_tonnes", label: "Quantity (t)", align: "right", render: (r) => num(r.quantity_tonnes) },
    {
      key: "biological",
      label: "Origin",
      render: (r) => biologicalBadge(r),
    },
    {
      key: "secondary_content_tonnes",
      label: "Secondary content (t)",
      align: "right",
      render: (r) => {
        const q = Number(r.quantity_tonnes);
        const s = Number(r.secondary_content_tonnes);
        const share = q > 0 && !isNaN(s) ? (s / q) * 100 : null;
        return (
          <span>
            <Data color={C.body}>{fmt(r.secondary_content_tonnes)}</Data>
            {share !== null && (
              <Data size={10} color={C.muted}> ({fmt(share, 1)}%)</Data>
            )}
          </span>
        );
      },
    },
    {
      key: "measurement_method",
      label: "Method",
      render: (r) => <Data color={C.body}>{methodLabel(r.measurement_method)}</Data>,
    },
  ];

  const productColumns = [
    {
      key: "product_line",
      label: "Product line",
      render: (r) => <Data>{r.product_line || "—"}</Data>,
    },
    {
      key: "facility_id",
      label: "Facility",
      render: (r) => <Data color={C.body}>{r.facility_id || "—"}</Data>,
    },
    { key: "quantity_tonnes", label: "Outflow (t)", align: "right", render: (r) => num(r.quantity_tonnes) },
    {
      key: "durability",
      label: "Durability vs industry (yr)",
      render: (r) => {
        const below =
          r.expected_durability_years !== null && r.expected_durability_years !== undefined &&
          r.industry_avg_durability_years !== null && r.industry_avg_durability_years !== undefined &&
          Number(r.expected_durability_years) < Number(r.industry_avg_durability_years);
        return (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <Data color={C.body}>
              {fmt(r.expected_durability_years, 1)} / {fmt(r.industry_avg_durability_years, 1)}
            </Data>
            {below && <Badge c={C.accent} Icon={AlertTriangle} label="Below avg" />}
          </span>
        );
      },
    },
    {
      key: "recyclable_content_product_pct",
      label: "Recyclable product (%)",
      align: "right",
      render: (r) => <Data color={C.body}>{pct(r.recyclable_content_product_pct)}</Data>,
    },
    {
      key: "recyclable_content_packaging_pct",
      label: "Recyclable packaging (%)",
      align: "right",
      render: (r) => <Data color={C.body}>{pct(r.recyclable_content_packaging_pct)}</Data>,
    },
  ];

  const formToggle = (label) => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
      <Overline Icon={Plus} color={C.body} style={{ marginBottom: 0 }}>
        {label}
      </Overline>
      <Btn
        variant={showForm ? "ghost" : "secondary"}
        Icon={showForm ? X : Plus}
        onClick={() => setShowForm((s) => !s)}
      >
        {showForm ? "Cancel" : `Add ${tab === "waste" ? "waste record" : tab === "inflows" ? "inflow" : "product outflow"}`}
      </Btn>
    </div>
  );

  const formGrid = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: 14,
  };

  const wasteFormCard = (
    <Card>
      {formToggle("Record waste")}
      {showForm && (
        <form onSubmit={submitWaste} style={{ marginTop: 16 }}>
          <div style={formGrid}>
            <div>
              <Label>Facility ID *</Label>
              <input
                value={wasteForm.facility_id}
                onChange={(e) => setWasteField("facility_id", e.target.value)}
                required
                placeholder="plant-042"
                style={field}
              />
            </div>
            <div>
              <Label>Facility name</Label>
              <input
                value={wasteForm.facility_name}
                onChange={(e) => setWasteField("facility_name", e.target.value)}
                placeholder="Leipzig assembly plant"
                style={field}
              />
            </div>
            <div>
              <Label>Waste stream *</Label>
              <input
                value={wasteForm.waste_stream}
                onChange={(e) => setWasteField("waste_stream", e.target.value)}
                required
                placeholder="Aluminium offcuts"
                style={field}
              />
            </div>
            <div>
              <Label>EWC code</Label>
              <input
                value={wasteForm.ewc_code}
                onChange={(e) => setWasteField("ewc_code", e.target.value)}
                maxLength={12}
                placeholder="12 01 03"
                style={field}
              />
            </div>
            <div>
              <Label>Hazard class *</Label>
              <select
                value={wasteForm.hazard_class}
                onChange={(e) => setWasteField("hazard_class", e.target.value)}
                required
                style={field}
              >
                {HAZARD_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Treatment route *</Label>
              <select
                value={wasteForm.treatment_route}
                onChange={(e) => setWasteField("treatment_route", e.target.value)}
                required
                style={field}
              >
                {TREATMENT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Quantity (t) *</Label>
              <input
                type="number"
                min="0"
                step="any"
                value={wasteForm.quantity_tonnes}
                onChange={(e) => setWasteField("quantity_tonnes", e.target.value)}
                required
                placeholder="0"
                style={field}
              />
            </div>
            <div>
              <Label>Measurement method *</Label>
              <select
                value={wasteForm.measurement_method}
                onChange={(e) => setWasteField("measurement_method", e.target.value)}
                required
                style={field}
              >
                {METHOD_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
            <Btn
              type="submit"
              Icon={Plus}
              disabled={saving || !wasteForm.facility_id.trim() || !wasteForm.waste_stream.trim() || wasteForm.quantity_tonnes === ""}
            >
              {saving ? "Saving…" : "Record"}
            </Btn>
            <Btn variant="secondary" onClick={() => { setWasteForm(EMPTY_WASTE_FORM); setShowForm(false); }}>
              Reset
            </Btn>
          </div>
          <p style={{ fontFamily: SANS, fontSize: 11, color: C.body, margin: "10px 0 0" }}>
            Diversion from disposal is derived by the platform from the treatment route (ESRS E5-5 definition).
          </p>
        </form>
      )}
    </Card>
  );

  const inflowFormCard = (
    <Card>
      {formToggle("Record resource inflow")}
      {showForm && (
        <form onSubmit={submitInflow} style={{ marginTop: 16 }}>
          <div style={formGrid}>
            <div>
              <Label>Facility ID *</Label>
              <input
                value={inflowForm.facility_id}
                onChange={(e) => setInflowField("facility_id", e.target.value)}
                required
                placeholder="plant-042"
                style={field}
              />
            </div>
            <div>
              <Label>Material *</Label>
              <input
                value={inflowForm.material_name}
                onChange={(e) => setInflowField("material_name", e.target.value)}
                required
                placeholder="Recycled aluminium ingot"
                style={field}
              />
            </div>
            <div>
              <Label>Quantity (t) *</Label>
              <input
                type="number"
                min="0"
                step="any"
                value={inflowForm.quantity_tonnes}
                onChange={(e) => setInflowField("quantity_tonnes", e.target.value)}
                required
                placeholder="0"
                style={field}
              />
            </div>
            <div>
              <Label>Biological material *</Label>
              <select
                value={inflowForm.biological}
                onChange={(e) => setInflowField("biological", e.target.value)}
                required
                style={field}
              >
                <option value="false">No</option>
                <option value="true">Yes</option>
              </select>
            </div>
            <div>
              <Label>Sustainably sourced (biological only)</Label>
              <select
                value={inflowForm.sustainably_sourced}
                onChange={(e) => setInflowField("sustainably_sourced", e.target.value)}
                disabled={inflowForm.biological !== "true"}
                style={field}
              >
                <option value="">Not assessed</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </div>
            <div>
              <Label>Secondary content (t)</Label>
              <input
                type="number"
                min="0"
                step="any"
                value={inflowForm.secondary_content_tonnes}
                onChange={(e) => setInflowField("secondary_content_tonnes", e.target.value)}
                placeholder="0"
                style={field}
              />
            </div>
            <div>
              <Label>Measurement method *</Label>
              <select
                value={inflowForm.measurement_method}
                onChange={(e) => setInflowField("measurement_method", e.target.value)}
                required
                style={field}
              >
                {METHOD_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
            <Btn
              type="submit"
              Icon={Plus}
              disabled={
                saving ||
                !inflowForm.facility_id.trim() ||
                !inflowForm.material_name.trim() ||
                inflowForm.quantity_tonnes === "" ||
                Number(inflowForm.secondary_content_tonnes || 0) > Number(inflowForm.quantity_tonnes || 0)
              }
            >
              {saving ? "Saving…" : "Record"}
            </Btn>
            <Btn variant="secondary" onClick={() => { setInflowForm(EMPTY_INFLOW_FORM); setShowForm(false); }}>
              Reset
            </Btn>
          </div>
          <p style={{ fontFamily: SANS, fontSize: 11, color: C.body, margin: "10px 0 0" }}>
            Secondary content cannot exceed the material quantity; sustainable sourcing applies only to biological materials (ESRS E5-4).
          </p>
        </form>
      )}
    </Card>
  );

  const productFormCard = (
    <Card>
      {formToggle("Record product outflow")}
      {showForm && (
        <form onSubmit={submitProduct} style={{ marginTop: 16 }}>
          <div style={formGrid}>
            <div>
              <Label>Facility ID *</Label>
              <input
                value={productForm.facility_id}
                onChange={(e) => setProductField("facility_id", e.target.value)}
                required
                placeholder="plant-042"
                style={field}
              />
            </div>
            <div>
              <Label>Product line *</Label>
              <input
                value={productForm.product_line}
                onChange={(e) => setProductField("product_line", e.target.value)}
                required
                placeholder="Modular battery packs"
                style={field}
              />
            </div>
            <div>
              <Label>Outflow quantity (t) *</Label>
              <input
                type="number"
                min="0"
                step="any"
                value={productForm.quantity_tonnes}
                onChange={(e) => setProductField("quantity_tonnes", e.target.value)}
                required
                placeholder="0"
                style={field}
              />
            </div>
            <div>
              <Label>Expected durability (yr)</Label>
              <input
                type="number"
                min="0"
                step="any"
                value={productForm.expected_durability_years}
                onChange={(e) => setProductField("expected_durability_years", e.target.value)}
                placeholder="0"
                style={field}
              />
            </div>
            <div>
              <Label>Industry avg durability (yr)</Label>
              <input
                type="number"
                min="0"
                step="any"
                value={productForm.industry_avg_durability_years}
                onChange={(e) => setProductField("industry_avg_durability_years", e.target.value)}
                placeholder="0"
                style={field}
              />
            </div>
            <div>
              <Label>Recyclable content — product (%)</Label>
              <input
                type="number"
                min="0"
                max="100"
                step="any"
                value={productForm.recyclable_content_product_pct}
                onChange={(e) => setProductField("recyclable_content_product_pct", e.target.value)}
                placeholder="0–100"
                style={field}
              />
            </div>
            <div>
              <Label>Recyclable content — packaging (%)</Label>
              <input
                type="number"
                min="0"
                max="100"
                step="any"
                value={productForm.recyclable_content_packaging_pct}
                onChange={(e) => setProductField("recyclable_content_packaging_pct", e.target.value)}
                placeholder="0–100"
                style={field}
              />
            </div>
          </div>
          <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
            <Btn
              type="submit"
              Icon={Plus}
              disabled={saving || !productForm.facility_id.trim() || !productForm.product_line.trim() || productForm.quantity_tonnes === ""}
            >
              {saving ? "Saving…" : "Record"}
            </Btn>
            <Btn variant="secondary" onClick={() => { setProductForm(EMPTY_PRODUCT_FORM); setShowForm(false); }}>
              Reset
            </Btn>
          </div>
        </form>
      )}
    </Card>
  );

  return (
    <Page>
      {header}
      <ErrorBanner message={error} onDismiss={() => setError("")} />

      {loading && !disclosure ? (
        <div style={{ display: "grid", gap: 16 }}>
          <Skeleton h={140} />
          <Skeleton h={200} />
          <Skeleton h={200} />
        </div>
      ) : (
        <div style={{ display: "grid", gap: 16 }}>
          {/* E5 disclosure KPI strip */}
          <Card>
            <Overline Icon={Gauge}>E5-4 / E5-5 disclosure · {period}</Overline>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                gap: 20,
              }}
            >
              {kpis.map((k) => (
                <Metric key={k.label} label={k.label} value={k.value} unit={k.unit} color={k.color} />
              ))}
            </div>
            <div
              style={{
                marginTop: 16,
                display: "flex",
                gap: 20,
                flexWrap: "wrap",
                fontFamily: SANS,
                fontSize: 12,
                color: C.body,
              }}
            >
              {TREATMENT_OPTIONS.map((o) => (
                <span key={o.value}>
                  {o.label} <Data color={C.ink}>{fmt(routes[o.value])}</Data>
                  <Data size={10} color={C.muted}> t</Data>
                </span>
              ))}
            </div>
          </Card>

          {/* Recommendations */}
          {recommendations.length > 0 && (
            <Card>
              <Overline Icon={Lightbulb} color={C.body}>
                Recommendations
              </Overline>
              <div style={{ display: "grid", gap: 12 }}>
                {recommendations.map((rec, i) => (
                  <div
                    key={`${rec.esrs_reference}-${i}`}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 12,
                      flexWrap: "wrap",
                      paddingBottom: i < recommendations.length - 1 ? 12 : 0,
                      borderBottom: i < recommendations.length - 1 ? `1px solid ${C.border}` : "none",
                    }}
                  >
                    <Badge
                      c={rec.priority === "high" ? C.destructive : C.accent}
                      Icon={AlertTriangle}
                      label={rec.priority === "high" ? "High" : "Medium"}
                    />
                    <Data size={11} color={C.muted}>{rec.esrs_reference}</Data>
                    <div style={{ flex: "1 1 320px", fontFamily: SANS, fontSize: 13, color: C.ink, lineHeight: 1.5 }}>
                      {rec.recommendation}
                      <div style={{ color: C.body, marginTop: 2 }}>{rec.action}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          <TabBar
            tabs={[
              { id: "waste", label: "Waste", count: waste.length },
              { id: "inflows", label: "Resource inflows", count: inflows.length },
              { id: "products", label: "Product outflows", count: products.length },
            ]}
            active={tab}
            onChange={changeTab}
          />

          {tab === "waste" && (
            <>
              <Card>
                <Overline Icon={Trash2} color={C.body}>
                  Waste streams
                </Overline>
                {waste.length === 0 ? (
                  <EmptyState
                    Icon={Trash2}
                    title="No waste records yet"
                    body="Record waste streams by facility, hazard class and treatment route to build the ESRS E5-5 waste disclosure and the SFDR PAI 9 feed for this period."
                  />
                ) : (
                  <DataTable
                    columns={wasteColumns}
                    rows={waste}
                    keyField="record_id"
                    emptyLabel="No waste records"
                  />
                )}
              </Card>
              {wasteFormCard}
            </>
          )}

          {tab === "inflows" && (
            <>
              <Card>
                <Overline Icon={Boxes} color={C.body}>
                  Resource inflows
                </Overline>
                {inflows.length === 0 ? (
                  <EmptyState
                    Icon={Boxes}
                    title="No resource inflows yet"
                    body="Record material inflows with their biological origin, sustainable sourcing and secondary (recycled) content to build the ESRS E5-4 inflows disclosure."
                  />
                ) : (
                  <DataTable
                    columns={inflowColumns}
                    rows={inflows}
                    keyField="record_id"
                    emptyLabel="No resource inflows"
                  />
                )}
              </Card>
              {inflowFormCard}
            </>
          )}

          {tab === "products" && (
            <>
              <Card>
                <Overline Icon={Package} color={C.body}>
                  Product outflows
                </Overline>
                {products.length === 0 ? (
                  <EmptyState
                    Icon={Package}
                    title="No product outflows yet"
                    body="Record product lines leaving the entity with their expected durability and recyclable content to build the ESRS E5-5 products disclosure."
                  />
                ) : (
                  <DataTable
                    columns={productColumns}
                    rows={products}
                    keyField="record_id"
                    emptyLabel="No product outflows"
                  />
                )}
              </Card>
              {productFormCard}
            </>
          )}

          {/* Reporting-coverage footnote */}
          <div style={{ fontFamily: SANS, fontSize: 12, color: C.body, display: "flex", gap: 20, flexWrap: "wrap" }}>
            <span>
              Waste streams reporting <Data color={C.ink}>{fmt(dw.streams_reporting, 0)}</Data>
            </span>
            <span>
              Materials reporting <Data color={C.ink}>{fmt(d4.materials_reporting, 0)}</Data>
            </span>
            <span>
              Product lines reporting <Data color={C.ink}>{fmt(dp.lines_reporting, 0)}</Data>
            </span>
          </div>
        </div>
      )}
    </Page>
  );
}
